import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import { getHouseholdFoodContext } from "../utils/foodIntelligence";
import { suggestMealsForTonight } from "../utils/mealSuggestionIntelligence";

/* ════════════════════════════════════════════════════════════════════════
   ARA's picks for tonight — quiet, derived, resident decides.
   Suggests ONLY from the Family Recipe Book. Never auto-plans.
   ════════════════════════════════════════════════════════════════════════ */

const inter = { fontFamily: "Inter, system-ui, sans-serif" };
const serif = { fontFamily: "var(--ara-font-display), 'Times New Roman', serif" };

const TIME_OPTIONS = [
  { id: "15", label: "15 min", limit: 15 },
  { id: "30", label: "30 min", limit: 30 },
  { id: "45", label: "45 min", limit: 45 },
  { id: "norush", label: "No rush", limit: null },
];

export function MealSuggestions({ todayName, hasPlannedMeal, onAddRecipe, onBrowseRecipes }) {
  const { recipes, members, kitchenCookingFor, pantryItems, weeklyMealPlan, updateMealPlanDay, setMealSlotFromRecipe } = useHomeOSState();
  const [open, setOpen] = useState(false);
  const [timeId, setTimeId] = useState("norush");
  const [whyOpen, setWhyOpen] = useState(null);

  const ctx = useMemo(
    () => getHouseholdFoodContext(members, kitchenCookingFor),
    [members, kitchenCookingFor]
  );
  const timeLimit = TIME_OPTIONS.find(t => t.id === timeId)?.limit ?? null;

  const result = useMemo(() => {
    if (!open) return null;
    return suggestMealsForTonight({ recipes, ctx, pantryItems, timeLimit, weeklyMealPlan });
  }, [open, recipes, ctx, pantryItems, timeLimit, weeklyMealPlan]);

  const planRecipe = (pick) => {
    const recipe = (recipes || []).find(r => r.id === pick.recipeId);
    if (!recipe) return;
    updateMealPlanDay(todayName, { dinner: recipe.title, dinnerRecipeId: recipe.id, status: "Planned" });
    setMealSlotFromRecipe(todayName, "dinner", recipe);
    setOpen(false);
    toast.success(`${recipe.title} planned for tonight.`);
  };

  if (!open) {
    return (
      <div className="mt-6">
        {!hasPlannedMeal && (
          <p className="italic text-[14px] leading-[1.5]" style={{ ...serif, color: "var(--ara-ink-muted)" }}>
            Want a hand choosing dinner?
          </p>
        )}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`${hasPlannedMeal ? "" : "mt-2 "}text-[13.5px] min-h-[36px] transition-opacity hover:opacity-70`}
          style={{ ...inter, color: "var(--ara-ink)", fontWeight: 450, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 }}
          data-testid="meals-suggest-dinner-btn"
        >
          {hasPlannedMeal ? "Want another idea? →" : "Suggest dinner →"}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-8" data-testid="meals-ara-picks">
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
        <p className="text-[11px] tracking-[0.16em] uppercase" style={{ ...inter, color: "var(--ara-mod-meals, #7d8c6f)", fontWeight: 500 }}>
          ARA's picks for tonight
        </p>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[12.5px] min-h-[32px] transition-opacity hover:opacity-70"
          style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400, background: "none", border: "none", cursor: "pointer", padding: 0 }}
          data-testid="meals-ara-picks-close"
        >
          Not now
        </button>
      </div>

      {/* Time tonight — optional, quiet */}
      <div className="mt-4 flex flex-wrap items-baseline gap-3" data-testid="meals-time-tonight">
        <span className="text-[11px] tracking-[0.14em] uppercase" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 500 }}>
          Time tonight
        </span>
        <div className="flex flex-wrap gap-1.5">
          {TIME_OPTIONS.map(t => {
            const active = timeId === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTimeId(t.id)}
                className="px-3 py-1.5 min-h-[32px] rounded-full text-[12px] transition-opacity hover:opacity-80"
                style={{
                  ...inter, fontWeight: active ? 500 : 400,
                  background: active ? "var(--ara-ink)" : "transparent",
                  color: active ? "#FAF9F6" : "var(--ara-ink-muted)",
                  border: `1px solid ${active ? "var(--ara-ink)" : "var(--ara-line)"}`,
                  cursor: "pointer",
                }}
                aria-pressed={active}
                data-testid={`meals-time-${t.id}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Picks */}
      {result?.state === "empty-book" && (
        <div className="mt-6" data-testid="meals-picks-empty-book">
          <p className="italic text-[15px] leading-[1.55]" style={{ ...serif, color: "var(--ara-ink-muted)", maxWidth: "42ch" }}>
            Your Family Recipe Book needs a few favourites before ARA can help choose dinner.
          </p>
          <button
            type="button"
            onClick={onAddRecipe}
            className="mt-3 text-[13.5px] min-h-[36px] transition-opacity hover:opacity-70"
            style={{ ...inter, color: "var(--ara-ink)", fontWeight: 450, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 }}
            data-testid="meals-picks-add-recipe"
          >
            Add first recipe →
          </button>
        </div>
      )}

      {result?.state === "no-fit" && (
        <div className="mt-6" data-testid="meals-picks-no-fit">
          <p className="italic text-[15px] leading-[1.55]" style={{ ...serif, color: "var(--ara-ink-muted)", maxWidth: "42ch" }}>
            I couldn't find a good fit in your Family Recipe Book tonight.
          </p>
          <div className="mt-3 flex flex-wrap gap-x-6">
            <button type="button" onClick={onBrowseRecipes} className="text-[13.5px] min-h-[36px] transition-opacity hover:opacity-70" style={{ ...inter, color: "var(--ara-ink)", fontWeight: 450, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 }} data-testid="meals-picks-browse">
              Browse Recipe Book →
            </button>
            <button type="button" onClick={onAddRecipe} className="text-[13.5px] min-h-[36px] transition-opacity hover:opacity-70" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400, background: "none", border: "none", cursor: "pointer", padding: 0 }} data-testid="meals-picks-add">
              Add recipe →
            </button>
          </div>
        </div>
      )}

      {result?.state === "picks" && (
        <ul className="mt-4">
          {result.picks.map((p, i) => (
            <li
              key={p.recipeId}
              className={`py-5 ${i === 0 ? "border-t" : ""} border-b`}
              style={{ borderColor: "var(--ara-line-soft)" }}
              data-testid={`meals-pick-${p.recipeId}`}
            >
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <p className="text-[16px]" style={{ ...inter, color: "var(--ara-ink)", fontWeight: 500 }}>{p.title}</p>
                {p.meta && (
                  <span className="text-[12.5px] tabular-nums" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400 }}>{p.meta}</span>
                )}
              </div>
              <p className="mt-1.5 italic text-[13.5px] leading-[1.55]" style={{ ...serif, color: "var(--ara-ink-muted)", maxWidth: "48ch" }} data-testid={`meals-pick-line-${p.recipeId}`}>
                {p.line}
              </p>
              {p.cautions.map((c, idx) => (
                <p key={idx} className="mt-1 text-[12.5px] leading-[1.5]" style={{ ...inter, color: "var(--ara-ink-muted)", fontWeight: 400 }} data-testid={`meals-pick-caution-${p.recipeId}-${idx}`}>
                  {c}
                </p>
              ))}
              {whyOpen === p.recipeId && (
                <p className="mt-2 text-[13px] leading-[1.6]" style={{ ...inter, color: "var(--ara-ink-muted)", fontWeight: 400, maxWidth: "52ch" }} data-testid={`meals-pick-why-text-${p.recipeId}`}>
                  {p.why}
                </p>
              )}
              <div className="mt-3 flex flex-wrap items-baseline gap-x-6 gap-y-1">
                <button
                  type="button"
                  onClick={() => planRecipe(p)}
                  className="text-[13.5px] min-h-[36px] transition-opacity hover:opacity-70"
                  style={{ ...inter, color: "var(--ara-ink)", fontWeight: 450, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 }}
                  aria-label={`Plan ${p.title} for tonight`}
                  data-testid={`meals-pick-plan-${p.recipeId}`}
                >
                  Plan this →
                </button>
                <button
                  type="button"
                  onClick={() => setWhyOpen(whyOpen === p.recipeId ? null : p.recipeId)}
                  className="text-[12.5px] min-h-[36px] transition-opacity hover:opacity-70"
                  style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 400, background: "none", border: "none", cursor: "pointer", padding: 0 }}
                  aria-expanded={whyOpen === p.recipeId}
                  aria-label={`Why ${p.title}?`}
                  data-testid={`meals-pick-why-${p.recipeId}`}
                >
                  {whyOpen === p.recipeId ? "Hide" : "Why this? →"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
